import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Table, Input, Select, Checkbox, Modal, Button, Radio } from "antd";
import { EditOutlined, PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import FETCH from "../../../utils/fetch";
import NewLoader from "../../../components/styled-components/loader/NewLoader";
import { useHistory } from "react-router-dom";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";

const { Option } = Select;

const StyledTableContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid #f0f0f0;

  .ant-table-thead > tr > th {
    background: #f8f9fa;
    font-weight: 600;
    color: #4a5568;
    border-bottom: 2px solid #e2e8f0;
  }

  .interactive-cell {
    color: #2a3da3;
    text-decoration: underline;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    border-radius: 4px;
    
    &:hover {
      background: rgba(42, 61, 163, 0.05);
      color: #1a2a83;
    }
  }

  .override-active {
    font-style: italic;
    font-weight: bold;
    color: #b45309;
    background: #fef3c7 !important;
    border: 1px solid #fde68a;
  }
`;

const SummaryCard = styled.div`
  background: linear-gradient(135deg, #2a3da3 0%, #1a2a83 100%);
  color: white;
  padding: 32px;
  border-radius: 16px;
  margin-top: 40px;
  box-shadow: 0 10px 25px rgba(42, 61, 163, 0.3);

  .summary-title {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 24px;
    opacity: 0.9;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding-bottom: 12px;
  }

  .summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    font-size: 1.1rem;

    .label {
      opacity: 0.85;
      display: flex;
      align-items: center;
      color: #000000;
      gap: 8px;
    }

    .value {
      font-weight: 700;
      color: #000000;
      font-size: 1.4rem;
    }
  }
`;

const OverrideModalContent = styled.div`
  .section-title {
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .global-data {
    background: #f3f4f6;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    border: 1px dashed #d1d5db;

    .source-label {
      font-size: 0.8rem;
      color: #6b7280;
      margin-top: 4px;
    }
  }

  .contribution-details {
    margin-top: 12px;
    background: white;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;

    .contribution-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      border-bottom: 1px solid #f3f4f6;
      &:last-child { border-bottom: none; }
      
      .label { font-size: 0.8rem; color: #6b7280; }
      .value { font-size: 0.85rem; font-weight: 500; color: #374151; }
    }
  }
`;

export const AssetAssignRetirement = ({
  rows,
  data,
  handleCashModuleCreate,
  cbFormChange,
  allLiabilities,
  attributeValues,
  rateOfReturnData,
  incomeList = [],
  onNavigateToAddAsset
}) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const [currentPageIlliquid, setCurrentPageIlliquid] = useState(1);
  const [totalPagesIlliquid, setTotalPagesIlliquid] = useState(1);
  const [currentPageLiquid, setCurrentPageLiquid] = useState(1);
  const [totalPagesLiquid, setTotalPagesLiquid] = useState(1);

  const [illiquidAssetsList, setIlliquidAssetsList] = useState([]);
  const [liquidAssetsList, setLiquidAssetsList] = useState([]);
  const [dataSource, setDataSource] = useState(rows || []);

  const [cachedDataIlliquid, setCachedDataIlliquid] = useState({});
  const [cachedDataLiquid, setCachedDataLiquid] = useState({});

  const [overrideModalVisible, setOverrideModalVisible] = useState(false);
  const [currentAssetForOverride, setCurrentAssetForOverride] = useState(null);
  const [overrideValue, setOverrideValue] = useState(0);
  const [overrideFrequency, setOverrideFrequency] = useState("Annually");

  // Use a ref to always have the latest dataSource in async operations
  const dataSourceRef = useRef(rows || []);
  const liquidAssetsListRef = useRef(liquidAssetsList);
  const illiquidAssetsListRef = useRef(illiquidAssetsList);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  }, [dataSource]);

  useEffect(() => {
    liquidAssetsListRef.current = liquidAssetsList;
  }, [liquidAssetsList]);

  useEffect(() => {
    illiquidAssetsListRef.current = illiquidAssetsList;
  }, [illiquidAssetsList]);

  useEffect(() => {
    if (rows && rows.length > 0) {
      setDataSource(rows);
    } else if (data && Array.isArray(data.assetId) && data.assetId.length > 0) {
      const reconstructed = data.assetId.map((id, index) => {
        if (!id) return null;
        return {
          assetId: id,
          id: id,
          Strategy: data.Strategy ? data.Strategy[index] : null,
          annualSavings: data.annualSavings !== undefined ? data.annualSavings[index] : null,
          savings_override: data.savings_override !== undefined ? data.savings_override[index] : null,
          rateOfReturn: data.rateOfReturn !== undefined ? data.rateOfReturn[index] : null,
          saleYear: data.saleYear !== undefined ? data.saleYear[index] : null,
          costToSell: data.costToSell !== undefined ? data.costToSell[index] : null,
          futureValue: data.futureValue !== undefined ? data.futureValue[index] : null,
          netLumpSum: data.netLumpSum !== undefined ? data.netLumpSum[index] : null,
          amountUseForGoal: data.amountUseForGoal !== undefined ? data.amountUseForGoal[index] : null,
          listType: data.listType !== undefined ? data.listType[index] : null,
          taxStatus: data.taxStatus !== undefined ? data.taxStatus[index] : null,
          valueOfAssetGoalRate: data.valueOfAssetGoalRate !== undefined ? data.valueOfAssetGoalRate[index] : null,
          "Cost Basis": data["Cost Basis"] !== undefined ? data["Cost Basis"][index] : null,
          "Monetary Value": data["Monetary Value"] !== undefined ? data["Monetary Value"][index] : null,
          module: data.module !== undefined ? data.module[index] : (data.listType && data.listType[index] === "liquid" ? "Investment Portfolio" : null), // Fallback for stability
        };
      }).filter(item => item !== null);
      setDataSource(reconstructed);
    } else {
      setDataSource([]);
    }
  }, [rows, data]);

  const syncWithDataSource = (prevList) => {
    if (!prevList || prevList.length === 0) return prevList;
    return prevList.map(asset => {
      const existing = dataSourceRef.current.find(d => String(d.assetId) === String(asset.id));
      if (existing) {
        return {
          ...asset,
          Strategy: existing.Strategy !== undefined && existing.Strategy !== null ? existing.Strategy : asset.Strategy,
          // rateOfReturn: existing.rateOfReturn !== undefined && existing.rateOfReturn !== null ? existing.rateOfReturn : asset.rateOfReturn, // Allow local edit to persist
          annualSavings: existing.annualSavings !== undefined && existing.annualSavings !== null ? existing.annualSavings : asset.annualSavings,
          savings_override: existing.savings_override !== undefined ? existing.savings_override : asset.savings_override,
          saleYear: existing.saleYear !== undefined && existing.saleYear !== null ? existing.saleYear : asset.saleYear,
          costToSell: existing.costToSell !== undefined && existing.costToSell !== null ? existing.costToSell : asset.costToSell,
          valueOfAssetGoalRate: existing.valueOfAssetGoalRate !== undefined && existing.valueOfAssetGoalRate !== null ? existing.valueOfAssetGoalRate : asset["Monetary Value"],
          "Cost Basis": existing["Cost Basis"] !== undefined && existing["Cost Basis"] !== null ? existing["Cost Basis"] : asset["Cost Basis"]
        };
      }
      return asset;
    });
  };

  // Sync whenever dataSource OR lists change to ensure the view stays current
  useEffect(() => {
    if (dataSource.length > 0) {
      setLiquidAssetsList(prev => syncWithDataSource(prev));
      setIlliquidAssetsList(prev => syncWithDataSource(prev));
    }
  }, [dataSource, liquidAssetsList.length, illiquidAssetsList.length]);

  // Sync latest values from lists back into dataSource (fixes missing or stale data in Update case)
  // Sync latest values from lists back into dataSource (fixes missing or stale data in Update case)
  useEffect(() => {
    if (liquidAssetsList.length === 0 && illiquidAssetsList.length === 0) return;

    const combined = [...liquidAssetsList, ...illiquidAssetsList];
    let changed = false;
    const updatedDataSource = dataSource.map(item => {
      const matched = combined.find(a => String(a.id) === String(item.assetId));
      if (matched) {
        const freshValue = parseFloat(matched["Monetary Value"]) || 0;
        const currentVal = parseFloat(item.valueOfAssetGoalRate) || 0;

        // Recalculate FV based on current Age/Retirement settings using locally stored matched state
        const fv = calculateFV(matched);
        const currentFV = parseFloat(item.futureValue) || 0;
        const fvChanged = Math.abs(parseFloat(fv) - currentFV) > 0.01;

        const taxStatusMismatch = !item.taxStatus && matched.taxStatus;
        const costBasisMismatch = !item["Cost Basis"] && matched["Cost Basis"];

        // If the balance has changed OR Future Value changed (due to age/rate changes) OR metadata is missing, refresh everything
        if (Math.abs(freshValue - currentVal) > 0.01 || fvChanged || taxStatusMismatch || costBasisMismatch) {
          changed = true;
          let amountUse = 0;
          let netLumpSum = 0;
          if (item.listType === "liquid" || matched.listType === "liquid") {
            amountUse = parseFloat(fv);
          } else {
            const commission = parseFloat(item.costToSell || matched.costToSell || 6.0) / 100;
            netLumpSum = (item.Strategy || matched.Strategy) === "Sell to Fund" ? (parseFloat(fv) * (1 - commission)).toFixed(2) : 0;
            amountUse = (item.Strategy || matched.Strategy) === "Sell to Fund" ? parseFloat(netLumpSum) : 0;
          }
          return {
            ...item,
            valueOfAssetGoalRate: freshValue,
            futureValue: fv,
            netLumpSum: netLumpSum,
            amountUseForGoal: amountUse,
            taxStatus: matched.taxStatus,
            "Cost Basis": matched["Cost Basis"],
            "Monetary Value": freshValue,
          };
        }
      }
      return item;
    });

    if (changed) {
      setDataSource(updatedDataSource);
      cbFormChange(updatedDataSource);
    }
  }, [liquidAssetsList, illiquidAssetsList, data["Current Age"], data["Desired Retirement Age"]]);

  const getTaxStatus = (asset) => {
    // First, check if the asset has the explicit tax status field
    if (asset["What is the tax status of this account?"]) {
      const taxStatus = asset["What is the tax status of this account?"];

      // Map the tax status values to display-friendly names
      if (taxStatus === "Tax-Free (e.g., Roth IRA / Roth 401k)") {
        return "Roth IRA (Tax-Free)";
      } else if (taxStatus === "Tax-Deferred (e.g., Traditional IRA / 401k)") {
        return "Tax-Deferred 401k";
      } else if (taxStatus === "Taxable (e.g., Brokerage, Savings)") {
        // Further differentiate based on account type if available
        const type = (asset["Account Type/Titled"] || asset["Asset Type"] || "").toLowerCase();
        if (type.includes("bank") || type.includes("cash") || type.includes("savings")) {
          return "Taxable Cash";
        }
        return "Taxable Brokerage";
      }

      // If the value doesn't match expected formats, return it as-is
      return taxStatus;
    }

    // Fallback: derive from account/asset type if tax status field is not available
    const name = (asset["assetName"] || asset["Name of Asset"] || asset["asset"] || "").toLowerCase();
    const type = (asset["Account Type/Titled"] || asset["Asset Type"] || "").toLowerCase();

    if (name.includes("roth") || type.includes("roth")) return "Roth IRA (Tax-Free)";
    if (
      name.includes("401") ||
      name.includes("ira") ||
      name.includes("deferred") ||
      type.includes("401") ||
      type.includes("ira") ||
      type.includes("deferred")
    ) {
      return "Tax-Deferred 401k";
    }

    return "Taxable Brokerage";
  };

  const calculateTotalAnnualSavings = (asset) => {
    if (asset.annualSavings && !Array.isArray(asset.annualSavings)) {
      return parseFloat(asset.annualSavings) || 0;
    }

    if (Array.isArray(asset.Amount) && Array.isArray(asset.Frequency)) {
      let total = 0;
      asset.Amount.forEach((amt, idx) => {
        const amount = parseFloat(amt) || 0;
        const freq = asset.Frequency[idx] || "Annually";
        if (freq === "Monthly") {
          total += amount * 12;
        } else {
          total += amount;
        }
      });
      return total;
    }

    return parseFloat(asset.annualSavings) || 0;
  };

  const getDefaultROR = useCallback((asset) => {
    const type = (asset["Account Type/Titled"] || asset["Asset Type"] || "").toLowerCase();
    const isCash = type.includes("bank") || type.includes("cash") || type.includes("savings");

    if (isCash) return 1.0;

    // Use Goal-level static percentage if provided, otherwise fallback to 6.0
    if (rateOfReturnData && rateOfReturnData.staticPercentage !== undefined && rateOfReturnData.staticPercentage !== null) {
      return parseFloat(rateOfReturnData.staticPercentage);
    }

    return 0; // Removed default 6.0
  }, [rateOfReturnData]);

  const getAssetsList = async (page) => {
    if (cachedDataIlliquid[page]) {
      updateIlliquidState(cachedDataIlliquid[page]);
      return;
    }
    try {
      setLoading(true);
      const id = JSON.parse(localStorage.getItem("accessId"));
      const userRole = JSON.parse(localStorage.getItem("role"));
      let url = "client-module-service/client-module/illiquid";
      let params = userRole === "Financial Advisor Client"
        ? `?page=${page}&limit=10&clientId=${id}`
        : `?page=${page}&limit=10`;

      let res = await FETCH.post({ url, id: params, body: {} });
      setLoading(false);
      if (res && res.records) {
        setCachedDataIlliquid(prev => ({ ...prev, [page]: res }));
        updateIlliquidState(res);
      }
    } catch (error) {
      console.error("Error fetching illiquid assets:", error);
      setLoading(false);
    }
  };

  const getAssetsLiquidList = async (page) => {
    if (cachedDataLiquid[page]) {
      updateLiquidState(cachedDataLiquid[page]);
      return;
    }
    try {
      setLoading(true);
      const id = JSON.parse(localStorage.getItem("accessId"));
      const userRole = JSON.parse(localStorage.getItem("role"));
      let url = "client-module-service/client-module/liquid";
      let params = userRole === "Financial Advisor Client"
        ? `?page=${page}&limit=10&clientId=${id}`
        : `?page=${page}&limit=10`;

      let res = await FETCH.post({ url, id: params, body: {} });
      setLoading(false);
      if (res && res.records) {
        setCachedDataLiquid(prev => ({ ...prev, [page]: res }));
        updateLiquidState(res);
      }
    } catch (error) {
      console.error("Error fetching liquid assets:", error);
      setLoading(false);
    }
  };

  const updateIlliquidState = (datares) => {
    const assets = (datares.records || []).map(asset => {
      const existing = dataSourceRef.current.find(d => String(d.assetId) === String(asset.id));
      const calculatedGlobalSavings = calculateTotalAnnualSavings(asset);
      const existingAnnualSavings = existing && existing.annualSavings !== undefined && existing.annualSavings !== null && !isNaN(parseFloat(existing.annualSavings)) ? existing.annualSavings : null;
      const existingOverride = existing && existing.savings_override !== undefined && existing.savings_override !== null && !isNaN(parseFloat(existing.savings_override)) ? existing.savings_override : null;

      return {
        ...asset,
        "Monetary Value": asset["Monetary Value"] || calculateMonetoryValue(asset),
        originalAnnualSavings: calculatedGlobalSavings,
        Strategy: (existing && existing.Strategy !== undefined && existing.Strategy !== null) ? existing.Strategy : (asset.Strategy || "Keep (Legacy)"),
        rateOfReturn: (existing && existing.rateOfReturn !== undefined && existing.rateOfReturn !== null) ? existing.rateOfReturn : (asset.rateOfReturn || getDefaultROR(asset)),
        annualSavings: existingAnnualSavings !== null ? existingAnnualSavings : calculatedGlobalSavings,
        saleYear: (existing && existing.saleYear !== undefined && existing.saleYear !== null) ? existing.saleYear : (asset.saleYear || 2035),
        costToSell: (existing && existing.costToSell !== undefined && existing.costToSell !== null) ? existing.costToSell : (asset.costToSell || 6.0),
        savings_override: existingOverride,
        taxStatus: getTaxStatus(asset),
        listType: "illiquid"
      };
    });
    setIlliquidAssetsList(assets);
    setTotalPagesIlliquid(Math.ceil(datares.pagination.total / (datares.pagination.limit || 10)));
  };

  const updateLiquidState = (datares) => {
    const assets = (datares.records || []).map(asset => {
      const existing = dataSourceRef.current.find(d => String(d.assetId) === String(asset.id));
      const calculatedGlobalSavings = calculateTotalAnnualSavings(asset);
      const existingAnnualSavings = existing && existing.annualSavings !== undefined && existing.annualSavings !== null && !isNaN(parseFloat(existing.annualSavings)) ? existing.annualSavings : null;
      const existingOverride = existing && existing.savings_override !== undefined && existing.savings_override !== null && !isNaN(parseFloat(existing.savings_override)) ? existing.savings_override : null;

      return {
        ...asset,
        "Monetary Value": asset["Monetary Value"] || calculateMonetoryValue(asset),
        originalAnnualSavings: calculatedGlobalSavings,
        Strategy: (existing && existing.Strategy !== undefined && existing.Strategy !== null) ? existing.Strategy : "Sell to Fund",
        rateOfReturn: (existing && existing.rateOfReturn !== undefined && existing.rateOfReturn !== null) ? existing.rateOfReturn : (asset.rateOfReturn || getDefaultROR(asset)),
        annualSavings: existingAnnualSavings !== null ? existingAnnualSavings : calculatedGlobalSavings,
        savings_override: existingOverride,
        taxStatus: getTaxStatus(asset),
        listType: "liquid"
      };
    });
    setLiquidAssetsList(assets);
    setTotalPagesLiquid(Math.ceil(datares.pagination.total / (datares.pagination.limit || 10)));
  };

  useEffect(() => {
    getAssetsList(currentPageIlliquid);
  }, [currentPageIlliquid]);

  useEffect(() => {
    getAssetsLiquidList(currentPageLiquid);
  }, [currentPageLiquid]);

  function calculateMonetoryValue(asset, targetDate = new Date()) {
    let Start =
      (asset["Cost Basis Date"] && new Date(asset["Cost Basis Date"])) ||
      new Date();
    const differenceInMs = targetDate.getTime() - Start.getTime();
    const daysDifference = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
    let totalDays = daysDifference / 365;
    let Cost = +asset["Cost Basis"] || 0;
    let ReturnRate = +asset["Static"] || 0;
    let decimalValue = ReturnRate ? ReturnRate / 100 : 0;
    const amount = 1 + decimalValue / 365;
    let power = Math.pow(amount, totalDays * 365);
    const estimateDiff = Cost * power;
    if (estimateDiff > 0 && !isNaN(estimateDiff)) {
      return parseFloat(estimateDiff).toFixed(2);
    } else {
      return 0;
    }
  }

  const calculateFV = useCallback((record) => {
    const currentAge = parseFloat(data["Current Age"]) || 0;
    const retirementAge = parseFloat(data["Desired Retirement Age"]) || 0;
    const t = Math.max(0, retirementAge - currentAge);

    // Check both potential balance fields to avoid 0 PV when fields vary
    const PV = parseFloat(record["Monetary Value"] || record.valueOfAssetGoalRate) || 0;

    // For illiquid assets, use costToSell as the growth rate. For liquid, use rateOfReturn.
    const growthRate = record.listType === "illiquid" ? (record.costToSell || 0) : (record.rateOfReturn || 0);
    const r = (parseFloat(growthRate) || 0) / 100;

    const PMT = record.listType === "liquid"
      ? (record.savings_override !== undefined && record.savings_override !== null
        ? parseFloat(record.savings_override)
        : (parseFloat(record.annualSavings) || 0))
      : 0;

    if (r === 0) return (PV + (PMT * t)).toFixed(2);
    const fv = PV * Math.pow(1 + r, t) + PMT * ((Math.pow(1 + r, t) - 1) / r);
    return isNaN(fv) ? "0.00" : fv.toFixed(2);
  }, [data]);

  const handleInputChange = useCallback((id, field, value, listType) => {
    // Keep the raw value for numeric fields to allow proper typing (e.g., "5.", "0.5", etc.)
    // Only parse to number when empty or when we need to ensure it's a valid number
    let finalValue = value;
    if (field === "rateOfReturn" || field === "annualSavings" || field === "costToSell" || field === "saleYear") {
      // If value is empty string, set to empty string (will be treated as 0 in calculations)
      // Otherwise keep the string value as-is for the input field
      if (value === "" || value === null || value === undefined) {
        finalValue = "";
      } else {
        // Keep as string to preserve user input like "5." or leading zeros during typing
        finalValue = value;
      }
    }
    const setter = listType === "liquid" ? setLiquidAssetsList : setIlliquidAssetsList;

    // Update local state only - defer dataSource updates to onBlur for performance and to keep focus
    setter(prevList => prevList.map(item => item.id === id ? { ...item, [field]: finalValue } : item));
  }, []);

  const handleInputBlur = useCallback((id, field, value, listType) => {
    // Normalize the value when user finishes editing
    // Convert empty strings or invalid numbers to 0
    const numericValue = parseFloat(value);
    let normalizedValue = isNaN(numericValue) ? 0 : numericValue;

    // Format with appropriate decimal places based on field type
    if (field === "rateOfReturn" || field === "costToSell" || field === "annualSavings") {
      // Format to 2 decimal places for percentages and monetary values
      normalizedValue = normalizedValue.toFixed(2);
    } else if (field === "saleYear") {
      // Keep whole numbers for years
      normalizedValue = Math.round(normalizedValue);
    }

    // Update with the normalized numeric value
    const setter = listType === "liquid" ? setLiquidAssetsList : setIlliquidAssetsList;

    // Use refs for lists to avoid dependency on volatile state list
    const list = listType === "liquid" ? liquidAssetsListRef.current : illiquidAssetsListRef.current;

    const updatedList = list.map(item => item.id === id ? { ...item, [field]: normalizedValue } : item);
    setter(updatedList);

    // Also update dataSource if this asset is selected
    // Use ref to avoid dependency on dataSource
    const dataSource = dataSourceRef.current;

    if (dataSource.some(item => item.assetId === id)) {
      const updatedDataSource = dataSource.map(item => {
        if (item.assetId === id) {
          // Find updated item from updated local list
          const matched = updatedList.find(a => a.id === id);
          if (!matched) return item;

          const fv = calculateFV(matched);
          let amountUse = 0;
          let netLumpSum = 0;
          if (listType === "liquid") {
            amountUse = parseFloat(fv);
          } else {
            const commission = parseFloat(matched.costToSell || 6.0) / 100;
            netLumpSum = matched.Strategy === "Sell to Fund" ? (parseFloat(fv) * (1 - commission)).toFixed(2) : 0;
            amountUse = matched.Strategy === "Sell to Fund" ? parseFloat(netLumpSum) : 0;
          }
          return {
            ...item,
            [field]: normalizedValue,
            futureValue: fv,
            netLumpSum,
            amountUseForGoal: amountUse,
            Strategy: matched.Strategy,
            rateOfReturn: matched.rateOfReturn,
            annualSavings: matched.annualSavings,
            saleYear: matched.saleYear,
            costToSell: matched.costToSell,
            taxStatus: getTaxStatus(matched),
            "Monetary Value": matched["Monetary Value"] || item.valueOfAssetGoalRate,
            "Cost Basis": matched["Cost Basis"] || item["Cost Basis"]
          };
        }
        return item;
      });
      setDataSource(updatedDataSource);
      cbFormChange(updatedDataSource);
    }
  }, [calculateFV, getTaxStatus]);

  const handleInputFocus = useCallback((e) => {
    // Only select all text if the value is 0 or empty
    // This makes it easy to replace 0, but allows normal editing for other values
    const value = parseFloat(e.target.value);
    if (value === 0 || e.target.value === "" || e.target.value === null || e.target.value === undefined) {
      e.target.select();
    }
  }, []);

  const formatInputValue = useCallback((value, field) => {
    // Format the value for display in input fields
    if (value === "" || value === null || value === undefined) {
      return "";
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return "";
    }

    // Format based on field type
    if (field === "rateOfReturn" || field === "costToSell") {
      // Show 2 decimal places for percentages
      return numValue.toFixed(2);
    } else if (field === "saleYear") {
      // Show whole number for years
      return Math.round(numValue).toString();
    }
    return value;
  }, []);


  const openOverrideModal = useCallback((record) => {
    setCurrentAssetForOverride(record);
    const globalValue = parseFloat(record.annualSavings) || 0;
    const initialVal = record.savings_override !== undefined && record.savings_override !== null
      ? parseFloat(record.savings_override)
      : globalValue;
    setOverrideValue(initialVal);
    setOverrideFrequency("Annually");
    setOverrideModalVisible(true);
  }, []);

  const applyOverride = () => {
    const val = parseFloat(overrideValue) || 0;
    const finalValue = overrideFrequency === "Monthly" ? (val * 12).toFixed(2) : val.toFixed(2);
    setLiquidAssetsList(prev => prev.map(item => item.id === currentAssetForOverride.id ? { ...item, savings_override: finalValue, annualSavings: finalValue } : item));
    const dataSource = dataSourceRef.current;
    const updatedDataSource = dataSource.map(item => {
      if (item.assetId === currentAssetForOverride.id) {
        const updatedAsset = { ...currentAssetForOverride, savings_override: finalValue, annualSavings: finalValue };
        const fv = calculateFV(updatedAsset);
        return { ...item, savings_override: finalValue, annualSavings: finalValue, futureValue: fv, amountUseForGoal: fv };
      }
      return item;
    });
    setDataSource(updatedDataSource);
    cbFormChange(updatedDataSource);
    setOverrideModalVisible(false);
  };

  const resetToGlobal = () => {
    const globalValue = currentAssetForOverride.originalAnnualSavings || 0;
    setLiquidAssetsList(prev => prev.map(item => item.id === currentAssetForOverride.id ? { ...item, savings_override: null, annualSavings: globalValue } : item));
    const updatedDataSource = dataSource.map(item => {
      if (item.assetId === currentAssetForOverride.id) {
        const updatedAsset = { ...currentAssetForOverride, savings_override: null, annualSavings: globalValue };
        const fv = calculateFV(updatedAsset);
        return { ...item, savings_override: null, annualSavings: globalValue, futureValue: fv, amountUseForGoal: fv };
      }
      return item;
    });
    setDataSource(updatedDataSource);
    cbFormChange(updatedDataSource);
    setOverrideModalVisible(false);
  };

  const handleAdd = useCallback((record, listType) => {
    const fv = calculateFV(record);
    let amountUse = 0;
    let netLumpSum = 0;
    if (listType === "liquid") {
      amountUse = parseFloat(fv);
    } else {
      const commission = parseFloat(record.costToSell || 6.0) / 100;
      netLumpSum = record.Strategy === "Sell to Fund" ? (parseFloat(fv) * (1 - commission)).toFixed(2) : 0;
      amountUse = record.Strategy === "Sell to Fund" ? parseFloat(netLumpSum) : 0;
    }
    const newData = { key: record.id, assetId: record.id, priority: dataSource.length + 1, assetName: record["Name of Asset"], asset: record["Name of Asset"], valueOfAssetGoalRate: record["Monetary Value"], annualSavings: record.annualSavings || 0, originalAnnualSavings: record.originalAnnualSavings || 0, savings_override: record.savings_override || null, rateOfReturn: record.rateOfReturn || getDefaultROR(record), Strategy: record.Strategy || (listType === "liquid" ? "Sell to Fund" : "Keep (Legacy)"), saleYear: record.saleYear || 0, costToSell: record.costToSell || 0, futureValue: fv, netLumpSum: netLumpSum, amountUseForGoal: amountUse, listType: listType, taxStatus: getTaxStatus(record), module: record.module };
    const updatedDataSource = [...dataSource, newData];
    setDataSource(updatedDataSource);
    cbFormChange(updatedDataSource);
  }, [dataSource, calculateFV, getDefaultROR, getTaxStatus]);

  const handleDelete = useCallback((record) => {
    const updatedDataSource = dataSource.filter(item => item.assetId !== record.id);
    setDataSource(updatedDataSource);
    cbFormChange(updatedDataSource);
  }, [dataSource]);

  const formatCurrency = useCallback((val) => {
    const num = parseFloat(val);
    if (val === undefined || val === null || isNaN(num)) return "$0.00";
    return "$" + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);

  const colsLiquidAssets = useMemo(() => [
    {
      title: "Include?", width: 80, render: (record) => {
        const isSelected = dataSource.some(item => item.assetId === record.id);
        return <Checkbox checked={isSelected} onChange={(e) => e.target.checked ? handleAdd(record, "liquid") : handleDelete(record)} />;
      }
    },
    { title: "Asset Name (Type)", render: (record) => (<div><div style={{ fontWeight: 600, color: "#1f2937" }}>{record["Name of Asset"]}</div><div style={{ fontSize: "11px", color: "#6b7280" }}>{getTaxStatus(record)}</div></div>) },
    { title: "Current Balance", dataIndex: "Monetary Value", render: (val) => formatCurrency(val) },
    {
      title: "Annual Savings (You + Employer)", render: (record) => {
        const isOverride = record.savings_override !== undefined && record.savings_override !== null;
        const displayValue = isOverride ? record.savings_override : record.annualSavings;
        const isSelected = dataSource.some(item => item.assetId === record.id);
        return (
          <div
            className={`interactive-cell ${isOverride ? 'override-active' : ''}`}
            onClick={() => isSelected && openOverrideModal(record)}
            style={{ opacity: isSelected ? 1 : 0.5, cursor: isSelected ? 'pointer' : 'not-allowed' }}
            title={isOverride ? `Custom value applied. Global: ${formatCurrency(record.originalAnnualSavings || 0)}` : (isSelected ? "Click to override global savings" : "Select asset to enable override")}
          >
            <span>{formatCurrency(displayValue)}</span>
            {isSelected && <EditOutlined style={{ marginLeft: 8, fontSize: 12 }} />}
          </div>
        );
      },
    },
    {
      title: "Est. Return", render: (record) => {
        const isSelected = dataSource.some(item => item.assetId === record.id);
        return (
          <Input
            type="number"
            size="small"
            style={{ width: 80 }}
            value={record.rateOfReturn === "" || record.rateOfReturn === null || record.rateOfReturn === undefined ? "" : record.rateOfReturn}
            onChange={(e) => handleInputChange(record.id, "rateOfReturn", e.target.value, "liquid")}
            onFocus={handleInputFocus}
            onBlur={(e) => handleInputBlur(record.id, "rateOfReturn", e.target.value, "liquid")}
            suffix="%"
          />
        );
      }
    },
    { title: `Future Value (at Age ${data["Desired Retirement Age"] || 65})`, render: (record) => { const fv = calculateFV(record); return <div style={{ fontWeight: 600, color: record.rateOfReturn > 0 ? "#059669" : "#6b7280" }}>{formatCurrency(fv)}</div>; } },
  ], [dataSource, handleAdd, handleDelete, handleInputChange, handleInputFocus, handleInputBlur, formatCurrency, getTaxStatus, formatInputValue, openOverrideModal, calculateFV, data]);

  const colsNonLiquidAssets = useMemo(() => [
    { title: "Include?", width: 80, render: (record) => { const isSelected = dataSource.some(item => item.assetId === record.id); return <Checkbox checked={isSelected} onChange={(e) => e.target.checked ? handleAdd(record, "illiquid") : handleDelete(record)} />; } },
    { title: "Asset Name", dataIndex: "Name of Asset", render: (text) => <div style={{ fontWeight: 500 }}>{text || "Unnamed Asset"}</div> },
    {
      title: "Projected Value at Retirement",
      render: (record) => {
        const fv = calculateFV(record);
        return <div style={{ fontWeight: 600, color: "#059669" }} title="Projected Future Value">
          {formatCurrency(fv)}
        </div>;
      }
    },
    { title: "Strategy", render: (record) => (<Select size="small" value={record.Strategy} style={{ width: 140 }} onChange={(val) => handleInputChange(record.id, "Strategy", val, "illiquid")}><Option value="Keep (Legacy)">Keep (Legacy)</Option><Option value="Sell to Fund">Sell to Fund</Option></Select>) },
    {
      title: "Sale Details", width: 200, render: (record) => {
        if (record.Strategy === "Sell to Fund") {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px 0' }}>
              <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Year:</span>
                <Input
                  type="number"
                  size="small"
                  value={record.saleYear === "" || record.saleYear === null || record.saleYear === undefined ? "" : record.saleYear}
                  style={{ width: 80 }}
                  onChange={(e) => handleInputChange(record.id, "saleYear", e.target.value, "illiquid")}
                  onFocus={handleInputFocus}
                  onBlur={(e) => handleInputBlur(record.id, "saleYear", e.target.value, "illiquid")}
                />
              </div>
              <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Cost:</span>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Input
                    type="number"
                    size="small"
                    value={record.costToSell === "" || record.costToSell === null || record.costToSell === undefined ? "" : record.costToSell}
                    style={{ width: 80 }}
                    onChange={(e) => handleInputChange(record.id, "costToSell", e.target.value, "illiquid")}
                    onFocus={handleInputFocus}
                    onBlur={(e) => handleInputBlur(record.id, "costToSell", e.target.value, "illiquid")}
                  />
                  <span style={{ marginLeft: '4px', color: '#6b7280' }}>%</span>
                </div>
              </div>
            </div>
          );
        }
        return <span style={{ color: '#9ca3af' }}>-- No Sale --</span>;
      },
    }
  ], [dataSource, handleAdd, handleDelete, handleInputChange, handleInputFocus, handleInputBlur, formatCurrency, formatInputValue, calculateFV, data]);

  const totalCurrentLiquid = dataSource.reduce((acc, curr) => acc + parseFloat(curr.valueOfAssetGoalRate || 0), 0);
  const totalProjectedLiquid = dataSource.reduce((acc, curr) => acc + parseFloat(curr.amountUseForGoal || 0), 0);
  const safeWithdrawalRate = totalProjectedLiquid * 0.04;

  return (
    <div className="asset-assign-retirement-container" style={{ width: "100%" }}>
      <StyledTableContainer>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", columnGap: "8px" }}><h3 className="incomecardheading" style={{ margin: 0 }}>Section 1: Liquid Investment Assets (The Funding Reservoir)</h3><ToolTipCreator name={"Goals_Liquid_Assets"} /></div>
        </div>
        <p style={{ color: '#6b7280', marginBottom: 20, fontSize: '0.95rem' }}>Use these accounts to draw monthly income during retirement.</p>
        <Table columns={colsLiquidAssets} dataSource={liquidAssetsList} rowKey="id" pagination={false} size="middle" className="retirement-asset-table" />
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>
          <Button type="dashed" icon={<PlusOutlined />}
            onClick={() => {
              if (onNavigateToAddAsset) {
                onNavigateToAddAsset();
              }
              history.push("/asset_allocation");
            }}
            style={{ borderRadius: 8, height: 40, paddingInline: 20 }}>Add New Asset...</Button>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Totals: <span style={{ color: '#2563eb' }}>{formatCurrency(totalCurrentLiquid)} (Today)</span><span style={{ margin: '0 12px', color: '#9ca3af' }}>→</span><span style={{ color: '#059669', fontSize: '1.1rem' }}>{formatCurrency(totalProjectedLiquid)} (Projected)</span></div>
        </div>
      </StyledTableContainer>
      <StyledTableContainer>
        <div style={{ display: "flex", alignItems: "baseline", columnGap: "8px", marginBottom: 8 }}><h3 className="incomecardheading" style={{ margin: 0 }}>Section 2: Illiquid Assets (Strategic Liquidation)</h3><ToolTipCreator name={"Goals_Illiquid_Assets"} /></div>
        <p style={{ color: '#6b7280', marginBottom: 20, fontSize: '0.95rem' }}>Real Estate, Business Interests, or Collectibles. You can hold these for net worth or sell them to generate cash.</p>
        <Table columns={colsNonLiquidAssets} dataSource={illiquidAssetsList} rowKey="id" pagination={false} size="middle" className="retirement-asset-table" />
      </StyledTableContainer>

      {/*incomeList && incomeList.length > 0 && (
        <StyledTableContainer>
          <div style={{ display: "flex", alignItems: "baseline", columnGap: "8px", marginBottom: 8 }}>
            <h3 className="incomecardheading" style={{ margin: 0 }}>Section 2.5: Portfolio-Independent Income Streams</h3>
            <ToolTipCreator name={"Retirement_Fixed_Income"} />
          </div>
          <p style={{ color: '#6b7280', marginBottom: 20, fontSize: '0.95rem' }}>These streams reduce the amount you need to withdraw from your investment accounts.</p>
          <Table
            size="middle"
            pagination={false}
            dataSource={incomeList}
            rowKey="id"
            columns={[
              { title: 'Income Source', dataIndex: 'Income Name', key: 'name', render: (t) => <div style={{ fontWeight: 500 }}>{t}</div> },
              { title: 'Type', dataIndex: 'Category', key: 'type' },
              { title: 'Amount', dataIndex: 'Gross Monthly Wages', key: 'amount', render: (v) => formatCurrency(v * 12) + " / year" }
            ]}
          />
        </StyledTableContainer>
      )*/}

      <SummaryCard>
        <div className="summary-title">Section 3: Summary of Resources</div>
        <div className="summary-item"><div className="label"><InfoCircleOutlined />Total Liquid "Nest Egg" at Retirement:</div><div className="value">{formatCurrency(totalProjectedLiquid)}</div></div>
        <div style={{ fontSize: '0.9rem', opacity: 0.8, marginBottom: 24, marginLeft: 28 }}>(Includes Investment Growth + Projected Proceeds from Asset Sales)</div>
        <div className="summary-item"><div className="label"><InfoCircleOutlined />Projected Safe Withdrawal Rate (4% Rule):</div><div className="value" style={{ color: '#4ade80' }}>{formatCurrency(safeWithdrawalRate)} / year</div></div>
      </SummaryCard>
      <Modal title={<div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}><EditOutlined style={{ color: '#2a3da3', fontSize: 20 }} /><span style={{ fontSize: 18 }}>Edit Savings for {currentAssetForOverride ? currentAssetForOverride["Name of Asset"] : ""}</span></div>} visible={overrideModalVisible} onCancel={() => setOverrideModalVisible(false)} width={500} centered footer={[<Button key="cancel" onClick={() => setOverrideModalVisible(false)} style={{ borderRadius: 6 }}>Cancel</Button>, (currentAssetForOverride && currentAssetForOverride.savings_override !== null) && (<Button key="reset" danger onClick={resetToGlobal} style={{ borderRadius: 6 }}>Reset to Global</Button>), <Button key="apply" type="primary" onClick={applyOverride} style={{ borderRadius: 6, background: '#2a3da3' }}>Apply Override</Button>]}>
        {currentAssetForOverride && (<OverrideModalContent>
          <div className="section-title"><InfoCircleOutlined style={{ color: '#2a3da3' }} />1. Current Source (The Truth):</div>
          <div className="global-data">
            <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '1.1rem' }}>Global Setting: {formatCurrency(currentAssetForOverride.originalAnnualSavings || 0)} / year</div>
            <div className="source-label">Linked to: {getTaxStatus(currentAssetForOverride)} (Asset Module)</div>

            {Array.isArray(currentAssetForOverride.Amount) && currentAssetForOverride.Amount.length > 0 && (
              <div className="contribution-details">
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>Consolidated Global Contributions:</div>
                {currentAssetForOverride.Amount.map((amt, idx) => (
                  <div key={idx} className="contribution-item">
                    <span className="label">
                      {currentAssetForOverride["Contribution or Distribution"] && currentAssetForOverride["Contribution or Distribution"][idx] || "Contribution"}
                      {currentAssetForOverride.Description && currentAssetForOverride.Description[idx] ? ` (${currentAssetForOverride.Description[idx]})` : ""}
                    </span>
                    <span className="value">
                      {formatCurrency(amt)} / {currentAssetForOverride.Frequency && currentAssetForOverride.Frequency[idx] || "Annually"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="section-title"><EditOutlined style={{ color: '#2a3da3' }} />2. Scenario Input (The Simulation):</div>
          <div style={{ marginBottom: 24 }}><label style={{ display: 'block', marginBottom: 10, fontSize: '0.9rem', color: '#4b5563', fontWeight: 500 }}>Projected Savings for this Goal:</label><Input type="number" prefix="$" size="large" value={overrideValue} onChange={(e) => setOverrideValue(parseFloat(e.target.value) || 0)} style={{ borderRadius: 8 }} /><p style={{ marginTop: 10, fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>Adjusting this value only affects your Retirement Goal. It does not change your main Budget or Asset records.</p></div>
          <div className="section-title">3. Frequency Toggle:</div>
          <Radio.Group value={overrideFrequency} onChange={(e) => setOverrideFrequency(e.target.value)} buttonStyle="solid" size="large" style={{ width: '100%', marginBottom: 16 }}><Radio.Button value="Monthly" style={{ width: '50%', textAlign: 'center' }}>Monthly</Radio.Button><Radio.Button value="Annually" style={{ width: '50%', textAlign: 'center' }}>Annually</Radio.Button></Radio.Group>
          {overrideFrequency === "Monthly" && (<div style={{ padding: '12px 16px', background: '#eef2ff', borderRadius: 8, color: '#2a3da3', fontWeight: 600, border: '1px solid #c7d2fe' }}>Annual Total: {formatCurrency(overrideValue * 12)} / year</div>)}
        </OverrideModalContent>)}
      </Modal>
      <NewLoader isLoading={loading} />
    </div>
  );
};
